/**
 * Die Filter der Radar-Liste in der Adresse.
 *
 * Reiter, Status, Suche, Sortierung und „Erledigte anzeigen“ stehen in der
 * URL: Ein Reload fängt nicht vorne an, die Radar-Mails verlinken eine
 * gefilterte Liste (`/radar?status=neu`), und „Zurück zum Radar“ sowie das
 * Zurück des Browsers bringen die Liste so zurück, wie man sie verlassen hat.
 * Bis zur Gegenprüfung D2 lagen Suche, Sortierung und „Erledigte“ nur im
 * State der Seite und waren nach jedem Besuch eines Eintrags weg.
 *
 * Standardwerte stehen nicht in der Adresse: `/radar` bleibt die Grundansicht.
 */

import type { RadarKind, RadarSort, RadarStatus } from '@/types';
import { SORT_TEXT, STATUS_REIHENFOLGE } from './radarText';

export type Reiter = 'alle' | RadarKind | 'akteure';

export const REITER: { wert: Reiter; label: string }[] = [
  { wert: 'alle', label: 'Alle' },
  { wert: 'foerderung', label: 'Förderung' },
  { wert: 'wettbewerb', label: 'Wettbewerbe' },
  { wert: 'event', label: 'Veranstaltungen' },
  { wert: 'akteure', label: 'Akteure' },
];

export interface ListenFilter {
  reiter: Reiter;
  status: RadarStatus | '';
  suche: string;
  sortierung: RadarSort;
  erledigte: boolean;
}

export const GRUNDANSICHT: ListenFilter = { reiter: 'alle', status: '', suche: '', sortierung: 'deadline', erledigte: false };

export function istReiter(wert: string | null): wert is Reiter {
  return REITER.some((r) => r.wert === wert);
}

function istStatus(wert: string | null): wert is RadarStatus {
  return STATUS_REIHENFOLGE.some((s) => s === wert);
}

function istSortierung(wert: string | null): wert is RadarSort {
  return wert !== null && Object.prototype.hasOwnProperty.call(SORT_TEXT, wert);
}

/** Die Filter aus der Adresse; Unbekanntes gilt als Grundansicht. */
export function filterAusAdresse(parameter: URLSearchParams): ListenFilter {
  const tab = parameter.get('tab');
  const status = parameter.get('status');
  const sort = parameter.get('sort');
  return {
    reiter: istReiter(tab) ? tab : GRUNDANSICHT.reiter,
    status: istStatus(status) ? status : GRUNDANSICHT.status,
    suche: parameter.get('suche') ?? GRUNDANSICHT.suche,
    sortierung: istSortierung(sort) ? sort : GRUNDANSICHT.sortierung,
    erledigte: parameter.get('erledigte') === '1',
  };
}

/** Die Adresse zu den Filtern, ohne Standardwerte. */
export function adresseAusFilter(filter: ListenFilter): Record<string, string> {
  const parameter: Record<string, string> = {};
  if (filter.reiter !== GRUNDANSICHT.reiter) parameter.tab = filter.reiter;
  if (filter.status) parameter.status = filter.status;
  if (filter.suche) parameter.suche = filter.suche;
  if (filter.sortierung !== GRUNDANSICHT.sortierung) parameter.sort = filter.sortierung;
  if (filter.erledigte) parameter.erledigte = '1';
  return parameter;
}
